import React, { useState, useEffect, useRef } from "react"
import { createRoot } from "react-dom/client"
import { ThemeProvider } from "styled-components"

import CitationModal from "./citation-modal"

// eslint-disable-next-line max-len
const checkIcon = `<svg width="11" height="10" viewBox="0 0 11 10" xmlns="http://www.w3.org/2000/svg"><path d="M10.5616 1.47255C10.7935 1.16327 10.7308 0.724508 10.4216 0.492549C10.1123 0.26059 9.67352 0.323271 9.44156 0.63255L4.41852 7.32996L2.03106 4.54448C1.77947 4.25094 1.33756 4.21694 1.04403 4.46853C0.750497 4.72012 0.716495 5.16203 0.968084 5.45556L3.77335 8.72851C3.81859 8.84955 3.89785 8.9595 4.0089 9.04279C4.09758 9.1093 4.19691 9.15159 4.29895 9.17078C4.51931 9.23643 4.76744 9.19227 4.955 9.03151C5.07764 8.92639 5.15498 8.78804 5.18511 8.64118L10.5616 1.47255Z" /></svg>`

const useCitations = (article, theme) => {
  const { elements, trustedCitationSources } = article

  const [citationList] = useState(() => {
    const storedCitations = {}
    let i = 1

    const addCitations = (element) => {
      // 1st iteration: gather all citation links and their arguments
      const allArgs = []
      element.text.replace(/<a([^>]*)href="(.*?)"([^>]*)>(.*?)<\/a>/g, (...args) => {
        const [match, p1, p2, p3, p4] = args

        const index = trustedCitationSources?.findIndex(({ publicationDomain }) =>
          p2.startsWith(publicationDomain),
        )
        if (index >= 0) {
          allArgs.push(args)
        }

        return match
      })

      // 2nd iteration: replace citation links with citation wrappers
      // (join consecutive citation links if they are the same)
      let idx = 0
      let currentText = ""
      element.text = element.text.replace(/<a([^>]*)href="(.*?)"([^>]*)>(.*?)<\/a>/g, (...args) => {
        const [match, p1, p2, p3, p4] = args

        let result = ""

        const sourceIndex = trustedCitationSources?.findIndex(({ publicationDomain }) =>
          p2.startsWith(publicationDomain),
        )
        if (sourceIndex >= 0) {
          const constructCitationWrapper = () => {
            let citationNumber = i
            if (p2 in storedCitations) {
              citationNumber = storedCitations[p2].number
            } else {
              storedCitations[p2] = { index: sourceIndex, number: citationNumber, url: p2 }
              i += 1
            }
            // eslint-disable-next-line max-len
            return `<span data-citation-wrapper><a${p1}href="${p2}"${p3} data-citation-link>${currentText}</a><a href="#citations" data-citation-number="${citationNumber}">${citationNumber}${checkIcon}</a></span>`
          }

          currentText += p4

          if (idx + 1 < allArgs.length) {
            const currentOffset = allArgs[idx][5]
            const currentLength = allArgs[idx][0].length
            const currentLink = allArgs[idx][1]
            const nextOffset = allArgs[idx + 1][5]
            const nextLink = allArgs[idx + 1][1]

            if (currentOffset + currentLength !== nextOffset || currentLink !== nextLink) {
              result = constructCitationWrapper()
              currentText = ""
            }
          } else {
            result = constructCitationWrapper()
            currentText = ""
          }

          idx += 1

          return result
        }

        return match
      })
    }

    elements.forEach((element) => {
      switch (element.type) {
        case "text":
        case "introduction":
        case "footer":
        case "text-and-image":
          addCitations(element)
          break
        case "list":
          element.items.forEach(addCitations)
          break
        default:
          break
      }
    })

    if (Object.keys(storedCitations).length > 0) {
      const items = Object.entries(storedCitations).map(([, { index, number, url }]) => ({
        ...trustedCitationSources[index],
        number,
        url,
      }))
      return items
    }

    return []
  })

  const intervalId = useRef()

  useEffect(() => {
    if (citationList.length > 0 && !intervalId.current) {
      intervalId.current = setInterval(() => {
        const wrapperNodes = document.documentElement.querySelectorAll("[data-citation-wrapper]")

        if (wrapperNodes.length > 0) {
          clearInterval(intervalId.current)

          wrapperNodes.forEach((wrapperNode) => {
            const [, numberNode] = wrapperNode.children
            const parent = document.body
            const container = document.createElement("div")
            container.style.position = "absolute"
            container.style.display = "none"
            parent.appendChild(container)
            const root = createRoot(container)

            const { citationNumber } = numberNode.dataset
            const citation = citationList[citationNumber - 1]
            const modal = (
              <ThemeProvider theme={theme}>
                <CitationModal
                  citation={citation}
                  onClose={() => {
                    container.style.display = "none"
                  }}
                />
              </ThemeProvider>
            )

            root.render(modal)

            const { tablet } = theme.breakpoints
            const isMobile = window.innerWidth < tablet
            if (!isMobile) {
              wrapperNode.onmouseenter = (event) => {
                // close all currently visible citation modals
                const modals = document.querySelectorAll("#citation-modal")
                modals.forEach((modal) => {
                  modal.parentElement.style.display = "none"
                })

                const { clientX: x, clientY: y } = event
                const { scrollTop } = document.documentElement
                const screenWidth =
                  window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth
                container.style.display = "block"
                const modalWidth = container.clientWidth
                container.style.zIndex = 1200
                container.style.top = `${scrollTop + y}px`
                if (x + modalWidth <= screenWidth) {
                  container.style.left = `${x - 60}px`
                  container.style.right = "unset"
                } else {
                  container.style.left = "unset"
                  container.style.right = "30px"
                }
                container.style.pointerEvents = "none"
              }

              wrapperNode.onmouseleave = (event) => {
                const { clientX: x, clientY: y } = event
                const { left, right, top, bottom } = container.getBoundingClientRect()
                const isMouseInModal = x >= left && x < right && y + 1 >= top && y < bottom
                if (!isMouseInModal) {
                  container.style.display = "none"
                }
              }

              container.onmouseleave = () => {
                container.style.display = "none"
              }

              numberNode.onclick = () => {
                const citationsNode = document.documentElement.querySelector("#citations")
                const detailsNode = citationsNode.children[0]
                const summaryNode = detailsNode.children[0]
                if (!detailsNode.open) {
                  summaryNode.click()
                }
              }
            } else {
              wrapperNode.ontouchend = (event) => {
                event.stopPropagation()
                event.preventDefault()
                const { bottom } = wrapperNode.getBoundingClientRect()
                const { scrollTop } = document.documentElement
                const screenWidth =
                  window.innerWidth ||
                  document.documentElement.clientWidth ||
                  document.body.clientWidth
                container.style.display = "block"
                const modalWidth = container.clientWidth
                container.style.zIndex = 1200
                container.style.top = `${scrollTop + bottom + 20}px`
                container.style.left = `${(screenWidth - modalWidth) / 2}px`
              }
            }
          })
        }
      }, 50)
    }
  }, [citationList, theme])

  return citationList
}

export default useCitations
